import { useState } from "react";
import { FIRMA_GOBERNADORA } from "../config";

/*
 * Los dos artes salen de branding/JGO, recortados al trazo (el original traía
 * ~27% de aire por lado dentro de un lienzo de 1000×1000). La proporción es la
 * misma en ambos, pero se guarda por separado porque los recortes no coinciden
 * al píxel y una firma estirada se nota.
 *
 * `ratio` = alto/ancho. Se usa para emitir width y height en el <img>: sin ellos
 * el pie da un salto de maquetación cuando carga la imagen.
 */
const ARTE = {
  // Tinta azul marino (#020942). Para fondos claros: la tarjeta de login y el
  // sidebar, que son --surface.
  clara: { src: "/assets/firma-gobernadora.png", ancho: 844, alto: 487 },
  // Tinta blanca. Hoy no la usa nadie; existe para que el par esté completo y
  // quien necesite firmar sobre un fondo oscuro no tenga que improvisar.
  oscura: { src: "/assets/firma-gobernadora-blanca.png", ancho: 927, alto: 535 },
} as const;

/**
 * Firma institucional de la gobernadora.
 *
 * No es un logotipo ni una marca de agua: es un pie de firma, y por eso siempre
 * viene acompañada del nombre y el cargo en texto. Sola, en la esquina de una
 * pantalla, una firma se lee como sello de propiedad; al pie de un bloque
 * institucional se lee como aval, que es lo que es.
 *
 * Se pinta en dos sitios y nada más —el pie del login y el pie del sidebar—
 * porque repetirla en cada sala la gastaría: lo que aparece doce veces deja de
 * ser una firma y pasa a ser fondo.
 */
export function FirmaGobernadora({
  ancho = 160,
  variante = "clara",
  rotulo,
  cargo = "Gobernadora de Puerto Rico",
  soloTrazo = false,
  className,
}: {
  ancho?: number;
  variante?: "clara" | "oscura";
  /** Rótulo institucional encima de la firma (p. ej. "Gobierno de Puerto Rico"). */
  rotulo?: string;
  cargo?: string;
  /**
   * Solo el trazo, sin nombre ni cargo. Es lo que pide la esquina del hero,
   * donde no cabe un bloque de texto encima de la foto.
   *
   * Cambia el trato de accesibilidad: sin texto al lado, la imagen deja de ser
   * decorativa y pasa a llevar un `alt` de verdad — si no, para un lector de
   * pantalla la firma sencillamente no existiría.
   */
  soloTrazo?: boolean;
  className?: string;
}) {
  // El hook va antes de cualquier return: si la llamada quedara detrás del flag,
  // encender o apagar FIRMA_GOBERNADORA cambiaría el número de hooks del árbol.
  const [falla, setFalla] = useState(false);

  if (!FIRMA_GOBERNADORA) return null;

  const arte = ARTE[variante];
  const alto = Math.round((ancho * arte.alto) / arte.ancho);

  if (soloTrazo) {
    // Sin texto detrás al que caer: si el arte no carga no se pinta nada, que es
    // mejor que un icono roto sobre la fotografía.
    if (falla) return null;
    return (
      <img
        className={`firma-gob-trazo${className ? ` ${className}` : ""}`}
        src={arte.src}
        alt="Firma de la gobernadora Jenniffer González Colón"
        width={ancho}
        height={alto}
        onError={() => setFalla(true)}
      />
    );
  }

  return (
    <div className={`firma-gob ${variante}${className ? ` ${className}` : ""}`}>
      {rotulo && <span className="firma-gob-rotulo">{rotulo}</span>}

      {/*
        alt="" a propósito: el nombre está en texto justo debajo, y repetirlo en
        el alt hace que el lector de pantalla lo diga dos veces seguidas. La
        imagen es decorativa; la información es el texto.

        Si el PNG no carga se esconde la imagen y el nombre se queda. La
        atribución no depende de que un asset llegue.
      */}
      {!falla && (
        <img
          className="firma-gob-trazo"
          src={arte.src}
          alt=""
          width={ancho}
          height={alto}
          onError={() => setFalla(true)}
        />
      )}

      <span className="firma-gob-nombre">Hon. Jenniffer González Colón</span>
      <span className="firma-gob-cargo">{cargo}</span>
    </div>
  );
}
